import React from 'react';
import { X } from 'lucide-react';
import { Avatar, Button, SearchInput, Select } from '../../../components/ui';
import { CATEGORIES, CATEGORY_LABEL, SEVERITIES, SEVERITY_LABEL } from '../format';
import type { SignalCategory, SignalSeverity, SignalStatus } from '../../../types/monitoring';
import { activeFilterCount, SignalFilters as Filters } from '../useMonitoring';
import { SubjectPicker } from './SubjectPicker';

const STATUS_OPTIONS: Array<{ value: Filters['status']; label: string }> = [
  { value: '', label: 'Any status' },
  { value: 'open', label: 'Open' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'dismissed', label: 'Dismissed' },
];

const DAY_OPTIONS: Array<{ value: Filters['days']; label: string }> = [
  { value: '', label: 'Any time' },
  { value: '1', label: 'Last 24h' },
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

const selectClass = 'basis-[calc(50%-0.25rem)] sm:basis-auto sm:w-[150px]';

export interface SignalFiltersProps {
  filters: Filters;
  onChange: (filters: Filters) => void;
  /** Hides the entity picker on a page that is already about one entity */
  hideSubject?: boolean;
  className?: string;
}

export const SignalFiltersBar: React.FC<SignalFiltersProps> = ({ filters, onChange, hideSubject, className = '' }) => {
  const set = <K extends keyof Filters>(key: K, value: Filters[K]) => onChange({ ...filters, [key]: value });
  const active = activeFilterCount(filters);

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          className="w-full sm:w-64"
          placeholder="Search signals"
          aria-label="Search signals"
          value={filters.search}
          onChange={(event) => set('search', event.target.value)}
        />
        {!hideSubject &&
          (filters.subject ? (
            <span className="inline-flex h-9 items-center gap-2 rounded-full bg-neutral-100 pl-1.5 pr-1 text-[13px] text-ink">
              <Avatar name={filters.subject.label} kind={filters.subject.type} size="xs" />
              <span className="max-w-[160px] truncate">{filters.subject.label}</span>
              <button
                type="button"
                onClick={() => set('subject', null)}
                aria-label={`Clear ${filters.subject.label}`}
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-200 hover:text-ink"
              >
                <X className="h-3.5 w-3.5" strokeWidth={1.75} />
              </button>
            </span>
          ) : (
            <SubjectPicker
              className="w-full sm:w-64"
              placeholder="Any entity"
              ariaLabel="Filter by entity"
              onPick={(subject) => set('subject', { type: subject.type, id: subject.id, label: subject.label })}
            />
          ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select
          className={selectClass}
          aria-label="Status"
          value={filters.status || ''}
          onChange={(event) => set('status', (event.target.value || '') as SignalStatus | 'open' | '')}
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.label} value={option.value || ''}>
              {option.label}
            </option>
          ))}
        </Select>

        <Select
          className={selectClass}
          aria-label="Severity"
          value={filters.severity || ''}
          onChange={(event) => set('severity', (event.target.value || '') as SignalSeverity | '')}
        >
          <option value="">Any severity</option>
          {SEVERITIES.map((severity) => (
            <option key={severity} value={severity}>
              {SEVERITY_LABEL[severity]}
            </option>
          ))}
        </Select>

        <Select
          className={selectClass}
          aria-label="Category"
          value={filters.category || ''}
          onChange={(event) => set('category', (event.target.value || '') as SignalCategory | '')}
        >
          <option value="">Any category</option>
          {CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {CATEGORY_LABEL[category]}
            </option>
          ))}
        </Select>

        <Select
          className={selectClass}
          aria-label="Date"
          value={filters.days}
          onChange={(event) => set('days', event.target.value as Filters['days'])}
        >
          {DAY_OPTIONS.map((option) => (
            <option key={option.label} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>

        {active > 0 && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() =>
              onChange({
                subject: hideSubject ? filters.subject : null,
                status: '',
                category: '',
                severity: '',
                days: '',
                search: '',
              })
            }
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
};
